/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import noop from 'lodash/noop';
import type {
	ConcreteRequest,
	Variables,
	OperationType,
	PreloadableConcreteRequest,
} from 'relay-runtime';
import type { Response as FetchQueryResponse } from '@jira/polaris__lib-relay-fetch/src/fetchQuery.types.tsx';
import { setMark, getTraceId } from '@jira/platform__early-script-utils/src/index.tsx';
import { FetchError } from '@jira/platform__early-script-utils/src/types.tsx';
import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';
import { earlyFetch } from '@jira/platform__graphql-relay-vendorless-utils/src/services/early-script-promise/index.tsx';
import {
	EarlyScriptQueryPromisesMap,
	getEarlyScriptPromiseKey,
} from '@jira/platform__graphql-relay-vendorless-promise/src/index.tsx';

type FetchDataParams = {
	url: string;
	body?: string;
	method?: string;
	headers?: Record<string, string | string[]>;
};

/**
 * Simplified fetch function to be used in early scripts.
 */
export const fetchData = <T,>({
	url,
	body,
	method = 'GET',
	headers,
}: FetchDataParams): Promise<T> => {
	setMark('jira.fe-spa.jpd.early-preload:start');
	return fetch(url, {
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			Accept: ['application/json', 'text/javascript'].join(','),
			...getActiveTraceHttpRequestHeaders(),
			...headers,
		},
		method,
		body,
	}).then((response) => {
		setMark('jira.fe-spa.jpd.early-preload:end');
		if (!response.ok) {
			const { status } = response;
			return response.text().then((str) => {
				const traceId = getTraceId(response);
				throw new FetchError(status, str, traceId || undefined);
			});
		}
		// oxlint-disable-next-line typescript/consistent-type-assertions
		return response.json() as T;
	});
};

// Replace with Promise.withResolvers once available
const withResolvers = <T,>() => {
	let resolve = noop;
	let reject = noop;
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});

	return {
		resolve,
		reject,
		promise,
	};
};

export async function fetchRelayData<ResponseJSON, TVariables extends Variables>(
	query: ConcreteRequest | PreloadableConcreteRequest<OperationType>,
	variables: TVariables,
): Promise<FetchQueryResponse<ResponseJSON>> {
	setMark('jira.fe-spa.jpd.early-preload:start');

	// earlyFetch is responsible for storing the promise in the cache map
	// and triggering request in the background
	earlyFetch(query, variables);

	// The cache map should already have the promise so we can work with it
	const promiseKey = getEarlyScriptPromiseKey(query.params, variables);
	const promise = EarlyScriptQueryPromisesMap.get(promiseKey);

	if (!promise) {
		throw new Error(`Early script promise not found: ${promiseKey}`);
	}

	// We want to quickly override cache map with a decorated promise that will be resolved later
	// after the actual response is received
	const { promise: decoratedPromise, resolve } = withResolvers<Response>();

	EarlyScriptQueryPromisesMap.set(promiseKey, decoratedPromise);

	const response = await promise;
	setMark('jira.fe-spa.jpd.early-preload:end');

	if (!response.ok) {
		resolve(response);
		const { status } = response;
		const text = await response.text();
		const traceId = getTraceId(response);
		throw new FetchError(status, text, traceId || undefined);
	}

	const responseJson: FetchQueryResponse<ResponseJSON> = await response.json();

	const decoratedResponse = Object.assign<Response, Partial<Response>>(response, {
		// Response.json() can be consumed only once, so we need to override it
		// with a new promise that will resolve to the actual data
		// so it can be called multiple times
		json: () => Promise.resolve(responseJson),
	});

	resolve(decoratedResponse);

	return responseJson;
}
